import type {
  AnalysisKind,
  AnalysisParameters,
  CoCitationKey,
  CountingMethod,
  KeywordSource,
  NormalizationMethod,
  TermLanguage,
} from '../../lib/types';
import type { MessageKey } from '../../i18n';

/**
 * The parameter surface of the six analyses, with VOSviewer's own defaults.
 *
 * The numbers here are not invented: they are the values `journalmap.pipeline`
 * uses when a caller passes nothing, which are in turn the values VOSviewer's
 * wizard pre-fills. Where the two differ the source file says so; they do not
 * differ for anything exposed here.
 */

/** VOSviewer's per-analysis "minimum number of ..." wizard defaults. */
export const DEFAULT_MIN_OCCURRENCES: Record<AnalysisKind, number> = {
  co_authorship: 5,
  keyword_co_occurrence: 5,
  terms: 10,
  citation: 5,
  bibliographic_coupling: 5,
  co_citation: 20,
};

/** Which noun the minimum-occurrence threshold counts, per analysis. It
 *  changes the label and the hint, not the parameter. */
export type OccurrenceNoun = 'documents' | 'occurrences' | 'citations';

export const OCCURRENCE_NOUN: Record<AnalysisKind, OccurrenceNoun> = {
  co_authorship: 'documents',
  keyword_co_occurrence: 'occurrences',
  terms: 'occurrences',
  citation: 'documents',
  bibliographic_coupling: 'documents',
  co_citation: 'citations',
};

/** Units of analysis offered per analysis. Empty means the analysis has no
 *  unit choice: keywords and terms are their own unit. */
export const UNITS: Record<AnalysisKind, readonly string[]> = {
  co_authorship: ['authors', 'organizations', 'countries'],
  keyword_co_occurrence: [],
  terms: [],
  citation: ['documents', 'sources', 'authors', 'organizations', 'countries'],
  bibliographic_coupling: ['documents', 'sources', 'authors', 'organizations', 'countries'],
  co_citation: ['references', 'authors', 'sources'],
};

export const DEFAULT_UNIT: Record<AnalysisKind, string> = {
  co_authorship: 'authors',
  keyword_co_occurrence: '',
  terms: '',
  citation: 'documents',
  bibliographic_coupling: 'documents',
  co_citation: 'references',
};

/** Which analyses take a counting method. Direct citation does not: it counts
 *  citation events, and there is nothing to fractionalise. */
export const HAS_COUNTING: Record<AnalysisKind, boolean> = {
  co_authorship: true,
  keyword_co_occurrence: true,
  terms: false,
  citation: false,
  bibliographic_coupling: true,
  co_citation: true,
};

/** i18n key for a unit name. Restricted to the units above. */
export function unitMessageKey(unit: string): MessageKey {
  switch (unit) {
    case 'organizations':
      return 'unit.organizations';
    case 'countries':
      return 'unit.countries';
    case 'documents':
      return 'unit.documents';
    case 'sources':
      return 'unit.sources';
    case 'references':
      return 'unit.references';
    default:
      return 'unit.authors';
  }
}

/** The hint under the unit selector depends on what the unit means here. */
export function unitHintKey(analysis: AnalysisKind): MessageKey {
  if (analysis === 'co_authorship') return 'param.unit.hint.co_authorship';
  if (analysis === 'co_citation') return 'param.unit.hint.co_citation';
  return 'param.unit.hint.citation';
}

export function analysisMessageKey(analysis: AnalysisKind): MessageKey {
  switch (analysis) {
    case 'keyword_co_occurrence':
      return 'analysis.keyword_co_occurrence';
    case 'terms':
      return 'analysis.terms';
    case 'citation':
      return 'analysis.citation';
    case 'bibliographic_coupling':
      return 'analysis.bibliographic_coupling';
    case 'co_citation':
      return 'analysis.co_citation';
    default:
      return 'analysis.co_authorship';
  }
}

export function analysisDescriptionKey(analysis: AnalysisKind): MessageKey {
  switch (analysis) {
    case 'keyword_co_occurrence':
      return 'analysis.keyword_co_occurrence.desc';
    case 'terms':
      return 'analysis.terms.desc';
    case 'citation':
      return 'analysis.citation.desc';
    case 'bibliographic_coupling':
      return 'analysis.bibliographic_coupling.desc';
    case 'co_citation':
      return 'analysis.co_citation.desc';
    default:
      return 'analysis.co_authorship.desc';
  }
}

export function minOccurrencesLabelKey(analysis: AnalysisKind): MessageKey {
  switch (OCCURRENCE_NOUN[analysis]) {
    case 'occurrences':
      return 'param.minOccurrences.label.occurrences';
    case 'citations':
      return 'param.minOccurrences.label.citations';
    default:
      return 'param.minOccurrences.label.documents';
  }
}

export function minOccurrencesHintKey(analysis: AnalysisKind): MessageKey {
  switch (OCCURRENCE_NOUN[analysis]) {
    case 'occurrences':
      return 'param.minOccurrences.hint.occurrences';
    case 'citations':
      return 'param.minOccurrences.hint.citations';
    default:
      return 'param.minOccurrences.hint.documents';
  }
}

/**
 * Every parameter the form can set, in one flat object.
 *
 * Holding all of them at once rather than one shape per analysis means
 * switching analysis keeps whatever the user already typed; `buildParameters`
 * picks out the fields that apply.
 */
export interface ParameterState {
  unit: string;
  counting: CountingMethod;
  keywordSource: KeywordSource;
  minOccurrences: number | null;
  minCitations: number | null;
  maxItems: number | null;

  useTitle: boolean;
  useAbstract: boolean;
  language: TermLanguage;
  binaryCounting: boolean;
  relevanceFraction: number | null;
  nTerms: number | null;

  cocitationKey: CoCitationKey;

  normalization: NormalizationMethod;
  resolution: number;
  minClusterSize: number;
  mergeSmallClusters: boolean;
  clusterRandomStarts: number;
  clusterIterations: number;
  attraction: number;
  repulsion: number;
  largestComponent: boolean;
  seed: number;
}

/** VOSviewer's defaults, all of them. */
export function defaultParameterState(analysis: AnalysisKind): ParameterState {
  return {
    unit: DEFAULT_UNIT[analysis],
    counting: 'full',
    keywordSource: 'both',
    minOccurrences: DEFAULT_MIN_OCCURRENCES[analysis],
    minCitations: 0,
    maxItems: null,

    useTitle: true,
    useAbstract: true,
    language: 'english',
    binaryCounting: true,
    relevanceFraction: 0.6,
    nTerms: null,

    cocitationKey: 'matchkey',

    normalization: 'association',
    resolution: 1,
    minClusterSize: 1,
    mergeSmallClusters: true,
    clusterRandomStarts: 10,
    clusterIterations: 10,
    attraction: 2,
    repulsion: 1,
    largestComponent: false,
    seed: 0,
  };
}

/**
 * Move the analysis-specific defaults across when the analysis changes, while
 * keeping anything the user deliberately set in the shared/advanced block.
 */
export function retargetParameterState(
  state: ParameterState,
  analysis: AnalysisKind,
): ParameterState {
  const units = UNITS[analysis];
  return {
    ...state,
    unit: units.includes(state.unit) ? state.unit : DEFAULT_UNIT[analysis],
    minOccurrences: DEFAULT_MIN_OCCURRENCES[analysis],
  };
}

/** Convert the form state into the request body the API forwards to Python. */
export function buildParameters(
  analysis: AnalysisKind,
  state: ParameterState,
): AnalysisParameters {
  const shared: AnalysisParameters = {
    min_occurrences: state.minOccurrences ?? DEFAULT_MIN_OCCURRENCES[analysis],
    min_citations: state.minCitations ?? 0,
    max_items: state.maxItems,
    normalization: state.normalization,
    resolution: state.resolution,
    min_cluster_size: state.minClusterSize,
    merge_small_clusters: state.mergeSmallClusters,
    n_random_starts: state.clusterRandomStarts,
    n_iterations: state.clusterIterations,
    attraction: state.attraction,
    repulsion: state.repulsion,
    largest_component: state.largestComponent,
    seed: state.seed,
  };

  if (UNITS[analysis].length > 0) shared.unit = state.unit;
  if (HAS_COUNTING[analysis]) shared.counting = state.counting;

  switch (analysis) {
    case 'keyword_co_occurrence':
      return { ...shared, keyword_source: state.keywordSource };

    case 'terms': {
      const fields: string[] = [];
      if (state.useTitle) fields.push('title');
      if (state.useAbstract) fields.push('abstract');
      return {
        ...shared,
        // An empty field list would extract nothing; fall back to VOSviewer's
        // own pair rather than asking the server to fail.
        fields: fields.length > 0 ? fields : ['title', 'abstract'],
        language: state.language,
        binary_counting: state.binaryCounting,
        relevance_fraction: state.relevanceFraction,
        // No n_terms. The API keeps a *fraction* of the ranked terms, which is
        // what VOSviewer does and what relevance_fraction above expresses;
        // there is no absolute-count parameter to send. The form no longer
        // offers one either — see ParameterForm.tsx.
      };
    }

    case 'co_citation':
      return { ...shared, cocitation_key: state.cocitationKey };

    default:
      return shared;
  }
}
